import React from 'react';
import { useShallow } from 'zustand/react/shallow';
import { showToast, Button } from '@attentive-platform/stem-ui';

import { GENERATE_ESTIMATE_PDF } from '../../../urls';
import { fetchApiData } from '../../../Utils/ApiCalls';
import { BackArrowIcon, GeneratePDFIcon } from '../rosettaSvgs';
import { useRosettaStore } from '../../../Stores/useRosettaStore';
import { getVolumeDiscount } from '../../../Utils/HelperFunctions';
import { calculatePricingHelper } from '../utils/rosettaPricingHelpers';

const PricingActions = () => {
  const {
    tradesData,
    volumeDiscountTiers,
    selectedTrades,
    companyName,
    projectsPerYear,
    sheetsPerProject,
    perTradeDistribution,
    manualDiscount,
    manualDiscountEnabled,
    selectedScopes,
    inflationPercentage,
    customScopes,
    loading,
    showTiersTable,
    hiddenTiers,
    dispatch
  } = useRosettaStore(
    useShallow(state => ({
      tradesData: state.tradesData,
      volumeDiscountTiers: state.volumeDiscountTiers,
      selectedTrades: state.selectedTrades,
      companyName: state.companyName,
      projectsPerYear: state.projectsPerYear,
      sheetsPerProject: state.sheetsPerProject,
      perTradeDistribution: state.perTradeDistribution,
      manualDiscount: state.manualDiscount,
      manualDiscountEnabled: state.manualDiscountEnabled,
      selectedScopes: state.selectedScopes,
      inflationPercentage: state.inflationPercentage,
      customScopes: state.customScopes,
      loading: state.loading,
      showTiersTable: state.showTiersTable,
      hiddenTiers: state.hiddenTiers,
      dispatch: state.dispatch
    }))
  );

  const totalAnnualSheets = projectsPerYear * sheetsPerProject;

  const pricing = calculatePricingHelper({
    selectedTrades,
    tradesData,
    selectedScopes,
    inflationPercentage,
    perTradeDistribution,
    totalAnnualSheets,
    volumeDiscountTiers
  });

  const manualDiscountPercent = manualDiscountEnabled ? manualDiscount / 100 : 0;
  const finalPrice = pricing.priceAfterVolumeDiscount * (1 - manualDiscountPercent);
  const annualCost = pricing.annualCostAfterVolumeDiscount * (1 - manualDiscountPercent);

  const handleGeneratePDF = async () => {
    try {
      if (!companyName) {
        showToast('Company name is required', 'error');
        return;
      }

      dispatch({ type: 'SET_LOADING', payload: true });

      const totalSheetsFromTrades = pricing.tradesData.reduce((sum, trade) => sum + trade.sheet_count, 0);

      const pricingWithoutManualDiscount = pricing;

      const volumeBasedPriceTiers = {};
      if (showTiersTable && typeof volumeDiscountTiers === 'object' && volumeDiscountTiers !== null) {
        const sortedTiers = Object.entries(volumeDiscountTiers)
          .map(([sheetCount, discount]) => ({
            minSheets: parseInt(sheetCount),
            discount
          }))
          .sort((a, b) => a.minSheets - b.minSheets);

        sortedTiers.forEach((tier, index) => {
          const sheets = tier.minSheets;
          if (sheets > 0 && !hiddenTiers.has(sheets)) {
            const volumeDiscount = tier.discount / 100;

            const pricePerSheet = pricingWithoutManualDiscount.blendedPrice * (1 - volumeDiscount);
            const annualCost = pricePerSheet * sheets;
            const quarterly = annualCost / 4;

            const nextTier = sortedTiers[index + 1];
            const upperBound = nextTier ? nextTier.minSheets : Infinity;
            const isActive = totalSheetsFromTrades >= tier.minSheets && totalSheetsFromTrades < upperBound;

            volumeBasedPriceTiers[sheets] = {
              price_per_sheet: parseFloat(pricePerSheet.toFixed(2)),
              annual_cost: Math.round(annualCost),
              quarterly: Math.round(quarterly),
              is_selected: isActive
            };
          }
        });
      }

      const trades = pricing.tradesData.map(trade => ({
        id: trade.trade_id,
        price_per_sheet: parseFloat(trade.trade_cost.toFixed(2)),
        sheet_count: trade.sheet_count,
        notes: customScopes[trade.trade_id] || '',
        scope_ids: trade.scope_ids
      }));

      const selectedTierEntry = Object.entries(volumeBasedPriceTiers).find(([, tier]) => tier.is_selected);
      const volumeTierName = selectedTierEntry ? `Tier ${Number(selectedTierEntry[0]).toLocaleString()}+` : '';

      const payload = {
        trades: trades,
        company_name: companyName,
        volume_tier: volumeTierName,
        annual_sheet_estimate: totalSheetsFromTrades,
        volume_based_price_tiers: volumeBasedPriceTiers,
        annual_price_estimate_total: Math.round(annualCost),
        quarterly_price_estimate_total: Math.round(annualCost / 4),
        manual_discount: manualDiscountEnabled ? manualDiscount : 0,
        blended_sheet_price: parseFloat(pricing.blendedPrice.toFixed(1)),
        annual_price_estimate_per_sheet: parseFloat(finalPrice.toFixed(1)),
        volume_discount: getVolumeDiscount(totalSheetsFromTrades, volumeDiscountTiers) * 100
      };

      const response = await fetchApiData({
        endpoint: GENERATE_ESTIMATE_PDF,
        apiRequest: 'POST',
        data: payload
      });

      if (response.error) {
        showToast(response.error, 'error');
        return;
      }

      showToast('PDF generated successfully!', 'success');

      if (response.data.download_url) {
        window.open(response.data.download_url, '_blank');
      }
    } catch (error) {
      showToast('Error generating PDF. Please try again.', 'error');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleBackToScope = () => {
    dispatch({ type: 'SET_SHOW_PRICING', payload: false });
  };

  return (
    <section className='rosetta-actions'>
      <Button variant='outlined' onClick={handleBackToScope} size='medium'>
        <BackArrowIcon />
        Back to Scope
      </Button>
      <Button
        size='medium'
        loading={loading}
        variant='contained'
        onClick={handleGeneratePDF}
        disabled={loading || selectedTrades.size === 0}>
        <GeneratePDFIcon />
        {loading ? 'Generating PDF...' : 'Generate Estimate PDF'}
      </Button>
    </section>
  );
};

export default PricingActions;
